import type { ModelInfo, Selection } from '../lib/api';

// ---------------------------------------------------------------------------
// The choices a model offers, as chips (204): a resolution or a quality, a
// duration, sound on or off, a scale. Only what the model prices is drawn —
// a model with one size shows no size row — and each chip carries its own
// credits so the creator can see what the choice costs before the quote
// line says the total.
// ---------------------------------------------------------------------------

export function ModelOptions({ model, value, onChange, bench }: {
  model: ModelInfo | null; value: Selection; onChange: (s: Selection) => void; bench: string;
}) {
  if (!model) return null;
  const o = model.options;
  const set = (patch: Selection) => onChange({ ...value, ...patch });
  const perSecond = model.unit === 'second';
  return (
    <div className="quick-chips" data-tool="model-options">
      {o.resolutions && o.resolutions.length > 1 && (
        <span className="quick-tier" role="radiogroup" aria-label="Size">
          {o.resolutions.map((r) => (
            <button key={r.id} type="button" role="radio" aria-checked={value.resolution === r.id} className={`chip${value.resolution === r.id ? ' on' : ''}`} onClick={() => set({ resolution: r.id })}>
              {r.label}<span className="quick-tier-price">{r.credits.toLocaleString()} cr{perSecond ? ' / s' : ''}</span>
            </button>
          ))}
        </span>
      )}
      {o.qualities && o.qualities.length > 1 && (
        <span className="quick-tier" role="radiogroup" aria-label="Quality">
          {o.qualities.map((r) => (
            <button key={r.id} type="button" role="radio" aria-checked={value.quality === r.id} className={`chip${value.quality === r.id ? ' on' : ''}`} onClick={() => set({ quality: r.id })}>
              {r.label}<span className="quick-tier-price">{r.credits.toLocaleString()} cr</span>
            </button>
          ))}
        </span>
      )}
      {o.scale && o.scale.length > 1 && (
        <span className="quick-tier" role="radiogroup" aria-label="Scale">
          {o.scale.map((s) => (
            <button key={s.x} type="button" role="radio" aria-checked={value.scale === s.x} className={`chip${value.scale === s.x ? ' on' : ''}`} onClick={() => set({ scale: s.x })}>
              ×{s.x}<span className="quick-tier-price">{s.credits.toLocaleString()} cr</span>
            </button>
          ))}
        </span>
      )}
      {o.durations && o.durations.length > 1 && bench === 'video' && (
        <label className="sub-dim">Length
          <select value={value.seconds ?? o.durations.find((d) => d.default)?.seconds ?? 8} onChange={(e) => set({ seconds: Number(e.target.value) })}>
            {o.durations.map((d) => <option key={d.seconds} value={d.seconds}>{d.seconds} s</option>)}
          </select>
        </label>
      )}
      {o.audio && model.constraints.audioConfigurable && (
        <button type="button" className={`chip${value.audio !== false ? ' on' : ''}`} aria-pressed={value.audio !== false} title="Sound on the shot" onClick={() => set({ audio: value.audio === false })}>
          {value.audio !== false ? 'Sound on' : 'Silent'}
          {o.audio.on !== null && o.audio.off !== null && <span className="quick-tier-price">{value.audio !== false ? `${o.audio.on.toLocaleString()} cr / s` : `${o.audio.off.toLocaleString()} cr / s`}</span>}
        </button>
      )}
      {o.tokens && (
        <span className="sub-dim">{o.tokens.input.toLocaleString()} cr / 1k tokens in · {o.tokens.output.toLocaleString()} out{o.tokens.cachedInput !== null ? ` · ${o.tokens.cachedInput.toLocaleString()} cached` : ''}</span>
      )}
    </div>
  );
}
